/**
 * Wall Newspaper — storage layer
 *
 * Two responsibilities:
 *   1. Pack/unpack `.tmb-board` ZIP files (JSZip lazy-loaded).
 *      Format: ZIP with `mimetype` (stored, EPUB-trick), `project.json`,
 *      `thumbnail.png`, `assets/{ulid}.{ext}`. Five-level validation on load
 *      so a single broken element does not block the rest.
 *
 *   2. IndexedDB auto-save. One database per tool, store `projects` keyed
 *      on project.meta.id, value = { id, title, modified_at, thumbnail_blob,
 *      project_zip_blob, size_bytes, version }. Cleanup keeps last 3.
 *      Quota-exceeded → drop oldest and retry once, then disable for session.
 *
 * Public API (window.WNStorage):
 *   - exportZip({project, assets}) → Promise<Blob>
 *   - importZip(blob) → Promise<{project, assets: Map<id, {blob, url}>}>
 *   - idbPut(record) → Promise
 *   - idbGet(id) → Promise<record | null>
 *   - idbList() → Promise<record[]>
 *   - idbDelete(id) → Promise
 *   - idbGetSetting(key) → Promise<value | null>
 *   - idbPutSetting(key, value) → Promise
 *   - canvasThumbnail(stage, maxW=800) → Promise<Blob>
 *
 * Errors thrown carry `.code` so the UI can show actionable messages:
 *   - INVALID_ZIP, MISSING_MIMETYPE, WRONG_MIMETYPE, MISSING_PROJECT,
 *     CORRUPT_PROJECT, MISSING_VERSION, NEWER_VERSION, IDB_OPEN_FAIL.
 */
(function () {
    'use strict';

    const MIMETYPE  = 'application/vnd.timbrica.board+zip';
    const VERSION   = '1.0';
    const DB_NAME   = 'timbrica_wall_newspaper';
    const DB_VER    = 2;
    const STORE_PROJ = 'projects';
    const STORE_SET  = 'settings';
    const STORE_TPL  = 'user_templates';
    const KEEP_PROJECTS = 3;
    const COMPRESS_LEVEL = 6; // balanced speed/size

    function err(code, msg) {
        const e = new Error(msg || code);
        e.code = code;
        return e;
    }

    // ─── JSZip lazy-load (paths from multi-track.blade.php) ───────────
    let _jszipPromise = null;
    function loadJSZip() {
        if (window.JSZip) return Promise.resolve(window.JSZip);
        if (_jszipPromise) return _jszipPromise;
        _jszipPromise = new Promise((resolve, reject) => {
            const tryUrl = (url, next) => {
                const s = document.createElement('script');
                s.src = url;
                s.onload = () => resolve(window.JSZip);
                s.onerror = () => {
                    if (next) next();
                    else reject(err('JSZIP_LOAD_FAIL', 'Could not load JSZip from any source'));
                };
                document.head.appendChild(s);
            };
            tryUrl('/vendor/jszip/jszip.min.js', () =>
                tryUrl('/cdn/jsdelivr/npm/jszip@3.10.1/dist/jszip.min.js', null));
        });
        return _jszipPromise;
    }

    // ─── ZIP IO ───────────────────────────────────────────────────────
    /**
     * @param {object} payload  { project, assets: Map<id, {blob, url}>, thumbnailBlob }
     * @returns {Promise<Blob>}
     */
    async function exportZip(payload) {
        const JSZip = await loadJSZip();
        const zip = new JSZip();

        // mimetype must come first, stored (no compression) — EPUB convention
        // so `file --mime-type` can sniff this format from the first bytes.
        zip.file('mimetype', MIMETYPE, { compression: 'STORE' });

        // Compute checksum over project.json sans the checksum field itself
        const projectClone = JSON.parse(JSON.stringify(payload.project));
        if (!projectClone.meta) projectClone.meta = {};
        projectClone.meta.checksum = null;
        const checksum = await sha256Hex(JSON.stringify(projectClone));
        projectClone.meta.checksum = checksum;
        zip.file('project.json', JSON.stringify(projectClone, null, 2));

        // Thumbnail
        if (payload.thumbnailBlob) zip.file('thumbnail.png', payload.thumbnailBlob);

        // Assets — only those still referenced by an element with type=image
        // (the caller passes the live assets Map; we trust its filtering).
        const used = collectUsedAssetIds(payload.project);
        for (const id of used) {
            const a = payload.assets?.get(id);
            if (a && a.blob) {
                const ext = extFromMime(a.blob.type) || 'bin';
                zip.file(`assets/${id}.${ext}`, a.blob);
            }
        }

        return zip.generateAsync({
            type: 'blob',
            compression: 'DEFLATE',
            compressionOptions: { level: COMPRESS_LEVEL },
            mimeType: MIMETYPE,
        });
    }

    /**
     * @param {Blob} blob
     * @returns {Promise<{project, assets: Map<id, {blob, url}>}>}
     */
    async function importZip(blob) {
        const JSZip = await loadJSZip();

        let zip;
        try { zip = await JSZip.loadAsync(blob); }
        catch (e) { throw err('INVALID_ZIP', e.message); }

        // L2: mimetype must exist and match
        const mtFile = zip.file('mimetype');
        if (!mtFile) throw err('MISSING_MIMETYPE');
        const mt = (await mtFile.async('string')).trim();
        if (!mt.startsWith(MIMETYPE)) throw err('WRONG_MIMETYPE', 'mime=' + mt);

        // L3: project.json
        const pjFile = zip.file('project.json');
        if (!pjFile) throw err('MISSING_PROJECT');
        let project;
        try { project = JSON.parse(await pjFile.async('string')); }
        catch (e) { throw err('CORRUPT_PROJECT', e.message); }

        // L4: version field
        if (!project.version) throw err('MISSING_VERSION');
        if (compareVersion(project.version, VERSION) > 0) {
            // Newer file — proceed but the caller should warn the user
            project._newer = true;
        }

        // L5: assets — load each blob, allocate object URL, mark broken refs.
        // JSZip records directory markers (the bare "assets/" entry) under
        // zip.files alongside real files; we must filter those out before
        // trying to read content, otherwise zip.file('assets/') returns null
        // and the next .async() call throws.
        const assets = new Map();
        const assetEntries = Object.keys(zip.files).filter((p) =>
            p.startsWith('assets/') && p !== 'assets/' && !zip.files[p].dir);
        for (const path of assetEntries) {
            const id = path.replace(/^assets\//, '').replace(/\.[^.]+$/, '');
            const file = zip.file(path);
            if (!file) continue; // defensive: directory or moved entry
            // JSZip hands the bytes back as a Blob WITHOUT a type. Raster images
            // survive that (the browser sniffs them); SVG does not, so a restored
            // board drew a broken photo and every frame threw (Sentry
            // TIMBRICA-FRONTEND-R1). The type is the one the name was written from.
            const ext = (path.match(/\.([^.\/]+)$/) || [])[1] || '';
            const raw = await file.async('blob');
            const blobAsset = raw.slice(0, raw.size, mimeFromExt(ext));
            const url = URL.createObjectURL(blobAsset);
            assets.set(id, { blob: blobAsset, url, source: 'imported' });
        }

        // Mark elements whose asset_id does not resolve (best-effort recovery —
        // do not throw; UI will render placeholder for these)
        if (Array.isArray(project.elements)) {
            for (const el of project.elements) {
                if (el.elType === 'image' && el.elAssetId && !assets.has(el.elAssetId)) {
                    el._broken = true;
                }
            }
        }

        return { project, assets };
    }

    function collectUsedAssetIds(project) {
        const ids = new Set();
        for (const el of (project.elements || [])) {
            if (el.elType === 'image' && el.elAssetId) ids.add(el.elAssetId);
        }
        if (project.canvas?.background?.image_asset) ids.add(project.canvas.background.image_asset);
        return ids;
    }

    function extFromMime(mime) {
        if (!mime) return 'bin';
        if (mime.includes('png')) return 'png';
        if (mime.includes('jpeg')) return 'jpg';
        if (mime.includes('webp')) return 'webp';
        if (mime.includes('gif')) return 'gif';
        if (mime.includes('svg')) return 'svg';
        if (mime.includes('avif')) return 'avif';
        if (mime.includes('heic') || mime.includes('heif')) return 'heic';
        return 'bin';
    }

    /** The inverse of extFromMime — the type an asset was saved under. */
    function mimeFromExt(ext) {
        switch (String(ext).toLowerCase()) {
            case 'png': return 'image/png';
            case 'jpg': return 'image/jpeg';
            case 'webp': return 'image/webp';
            case 'gif': return 'image/gif';
            case 'svg': return 'image/svg+xml';
            case 'avif': return 'image/avif';
            case 'heic': return 'image/heic';
            default: return '';
        }
    }

    function compareVersion(a, b) {
        const pa = String(a).split('.').map((n) => parseInt(n, 10) || 0);
        const pb = String(b).split('.').map((n) => parseInt(n, 10) || 0);
        for (let i = 0; i < 3; i++) {
            const da = pa[i] || 0, db = pb[i] || 0;
            if (da !== db) return da - db;
        }
        return 0;
    }

    async function sha256Hex(str) {
        const buf = new TextEncoder().encode(str);
        const hash = await crypto.subtle.digest('SHA-256', buf);
        return Array.from(new Uint8Array(hash)).map((b) => b.toString(16).padStart(2, '0')).join('');
    }

    // ─── IndexedDB ────────────────────────────────────────────────────
    let _dbPromise = null;
    function openDB() {
        if (_dbPromise) return _dbPromise;
        _dbPromise = new Promise((resolve, reject) => {
            const req = indexedDB.open(DB_NAME, DB_VER);
            req.onerror = () => reject(err('IDB_OPEN_FAIL', req.error?.message));
            req.onupgradeneeded = (e) => {
                const db = e.target.result;
                if (!db.objectStoreNames.contains(STORE_PROJ)) {
                    const s = db.createObjectStore(STORE_PROJ, { keyPath: 'id' });
                    s.createIndex('modified_at', 'modified_at');
                }
                if (!db.objectStoreNames.contains(STORE_SET)) {
                    db.createObjectStore(STORE_SET, { keyPath: 'key' });
                }
                if (!db.objectStoreNames.contains(STORE_TPL)) {
                    const s = db.createObjectStore(STORE_TPL, { keyPath: 'id' });
                    s.createIndex('created_at', 'created_at');
                }
            };
            req.onsuccess = () => resolve(req.result);
        });
        return _dbPromise;
    }

    function txWrap(storeName, mode, fn) {
        return openDB().then((db) => new Promise((resolve, reject) => {
            const tx = db.transaction(storeName, mode);
            const store = tx.objectStore(storeName);
            let result;
            try { result = fn(store, tx); } catch (e) { reject(e); return; }
            tx.oncomplete = () => resolve(result?.then ? result : (result?.target ? result.target.result : result));
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || err('IDB_ABORT'));
        }));
    }

    function reqToPromise(req) {
        return new Promise((resolve, reject) => {
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
        });
    }

    async function idbPut(record) {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_PROJ, 'readwrite');
            const store = tx.objectStore(STORE_PROJ);
            store.put(record);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || err('IDB_QUOTA'));
        });
    }

    async function idbGet(id) {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_PROJ, 'readonly');
            const req = tx.objectStore(STORE_PROJ).get(id);
            req.onsuccess = () => resolve(req.result || null);
            req.onerror = () => reject(req.error);
        });
    }

    async function idbList() {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_PROJ, 'readonly');
            const req = tx.objectStore(STORE_PROJ).getAll();
            req.onsuccess = () => {
                const all = req.result || [];
                all.sort((a, b) => (b.modified_at || 0) - (a.modified_at || 0));
                resolve(all);
            };
            req.onerror = () => reject(req.error);
        });
    }

    async function idbDelete(id) {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_PROJ, 'readwrite');
            tx.objectStore(STORE_PROJ).delete(id);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    async function idbGetSetting(key) {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_SET, 'readonly');
            const req = tx.objectStore(STORE_SET).get(key);
            req.onsuccess = () => resolve(req.result?.value ?? null);
            req.onerror = () => reject(req.error);
        });
    }

    async function idbPutSetting(key, value) {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_SET, 'readwrite');
            tx.objectStore(STORE_SET).put({ key, value });
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    // ─── User templates (saved by the user via "Save as template") ────
    // Schema per record: { id, name, created_at, thumbnail_blob, template_json }
    // template_json mirrors public template.json shape (no assets bundled —
    // user templates are layouts, not photo collections).
    async function idbPutTemplate(record) {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_TPL, 'readwrite');
            tx.objectStore(STORE_TPL).put(record);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }
    async function idbListTemplates() {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_TPL, 'readonly');
            const req = tx.objectStore(STORE_TPL).getAll();
            req.onsuccess = () => {
                const all = req.result || [];
                all.sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
                resolve(all);
            };
            req.onerror = () => reject(req.error);
        });
    }
    async function idbDeleteTemplate(id) {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_TPL, 'readwrite');
            tx.objectStore(STORE_TPL).delete(id);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
        });
    }

    /**
     * Trim `projects` store to the last KEEP_PROJECTS most-recently-modified.
     * Excludes `protectId` (the project we are about to save right now —
     * we never want to delete it from our own cleanup).
     */
    async function idbCleanup(protectId) {
        const all = await idbList();
        const kept = [];
        for (const rec of all) {
            if (kept.length < KEEP_PROJECTS || rec.id === protectId) {
                kept.push(rec);
            } else {
                try { await idbDelete(rec.id); } catch (e) { /* noop */ }
            }
        }
    }

    // ─── Thumbnail rendering ──────────────────────────────────────────
    /**
     * Snap a downscaled PNG of the canvas suitable for the recovery dialog
     * and the recent-projects grid.
     *
     * @param {Konva.Stage} stage
     * @param {number}      maxW  longest edge of the resulting PNG
     * @returns {Promise<Blob>}
     */
    async function canvasThumbnail(stage, maxW) {
        if (!stage) return null;
        const cap = maxW || 800;
        // stage.width()/height() are in screen-space when zoom != 1; we want
        // canvas-space dimensions. Keep this simple by snapshotting at current
        // scale and then downscaling via 2D context.
        const dataUrl = stage.toDataURL({ pixelRatio: 1 });
        const img = await new Promise((resolve, reject) => {
            const i = new Image();
            i.onload = () => resolve(i);
            i.onerror = reject;
            i.src = dataUrl;
        });
        const ratio = img.width / img.height;
        const w = Math.min(cap, img.width);
        const h = Math.round(w / ratio);
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        return new Promise((resolve) => c.toBlob(resolve, 'image/jpeg', 0.82));
    }

    // ─── Public API ───────────────────────────────────────────────────
    window.WNStorage = {
        // ZIP
        exportZip,
        importZip,
        // IDB
        idbPut, idbGet, idbList, idbDelete,
        idbGetSetting, idbPutSetting, idbCleanup,
        // User templates
        idbPutTemplate, idbListTemplates, idbDeleteTemplate,
        // Helpers
        canvasThumbnail,
        // Constants exposed for UI version messaging
        MIMETYPE, VERSION, KEEP_PROJECTS,
    };
})();
